import { PARTNERING_EXCHANGES } from "@/utils/exchangeUtils";

export enum ExchangeConnectionStatus {
  CONNECTED = "true",
  NOT_CONNECTED = "not-connected",
}

/**
 * Wrapper model which applies status to a certain exchange
 */
export abstract class Exchange {
  constructor(
    public name: string,
    public connectionStatus: ExchangeConnectionStatus
  ) { }

  public get isConnected(): boolean {
    return this.connectionStatus !== ExchangeConnectionStatus.NOT_CONNECTED;
  }

  public get isPartner(): boolean {
    return PARTNERING_EXCHANGES.includes(this.name);
  }

  public get isDisabled(): boolean {
    switch (this.name) {
      case "FTX": return !this.isConnected;
      default: return false;
    }
  }

  public get connectionWarning(): any {
    switch (this.name) {
      case "Kraken": return {
        label: "High Fees",
        labelIcon: "mdi-alert",
        labelColor: "error",
        tooltip: "Warning: high fees. Based on our data, the performance of AI trading bots on Kraken exchange is lower than on other exchanges. This may be due to the high taker fees (0.36%) and relatively low liquidity on Kraken. If you have accounts on multiple exchanges, we recommend using alternative options."
      }
      case "Bitvavo": return {
        label: "High Fees",
        labelIcon: "mdi-alert",
        labelColor: "error",
        tooltip: "Warning: high fees. Based on our data, the performance of AI trading bots on Bitvavo exchange is lower than on other exchanges. This may be due to the high taker fees (0.25%) on Bitvavo. If you have accounts on multiple exchanges, we recommend using alternative options."
      }
      case "FTX": return {
        label: "Temporary Unavailable",
        labelIcon: "mdi-info",
        labelColor: "secondary",
        tooltip: "Temporary unavailable due to recent news of bankruptcy",
        link: "https://www.reuters.com/business/ftx-scrambles-funds-regulators-take-action-2022-11-11/",
      }
      default: return null;
    }
  }

  public get statusString(): string {
    switch (this.connectionStatus) {
      case ExchangeConnectionStatus.CONNECTED:
        return "Connected";
      case ExchangeConnectionStatus.NOT_CONNECTED:
        return "Not Connected";
    }
  }
}

export class ConnectedExchange extends Exchange {
  constructor(
    public name: string,
    public connectionStatus: ExchangeConnectionStatus,
    public daysUntilTradingPermissionStops: number | null
  ) {
    super(name, connectionStatus);
  }

  public get automatedTradingStatusDetails(): any {
    if (!this.daysUntilTradingPermissionStops) {
      return { status: "Active", textColorClass: "green--text" };
    }
    let days: number = this.daysUntilTradingPermissionStops;
    switch (true) {
      case days > 20:
        return {
          status: "Active",
          textColorClass: "green--text",
          color: "green",
        };
      case days > 10:
        return {
          status: "Active",
          textColorClass: "orange--text lighten-2",
          color: "orange lighten-2",
        };
      case days > 0:
        return {
          status: "Active",
          textColorClass: "red--text lighten-1",
          color: "red lighten-1",
        };
      default:
        return {
          status: "Disabled",
          textColorClass: "red--text",
          color: "red",
        };
    }
  }
}

export class NotConnectedExchange extends Exchange {
  constructor(public name: string) {
    super(name, ExchangeConnectionStatus.NOT_CONNECTED);
  }
}
